import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FaCheck } from "react-icons/fa";
import PageBanner from "./PageBanner";
import Breadcrumbs from "./Breadcrumbs";
import JsonLd from "./JsonLd";
import { eventCategoryContent } from "../constants/eventsData";
import { eventCategorySeo } from "../constants/eventCategorySeo";
import { getCategoryAssets } from "../utils/categoryAssets";
import {
  pairSubeventsWithGallery,
  findWhyChooseImage,
} from "../utils/pairSubeventsWithGallery";
import { buildFaqPageJsonLd } from "../utils/faqJsonLd";
import { buildBreadcrumbListJsonLd } from "../utils/breadcrumbSchema";
import { buildCategoryPageGraph } from "../schema/categoryPageGraph";
import { subeventNameToSlug } from "../utils/eventSubeventSlug";
import "./homeGraphic.css";
import "./EventCategoryLayout.css";

const EventCategoryLayout = ({ categoryId }) => {
  const location = useLocation();
  const { bannerSrc, gallery } = useMemo(
    () => getCategoryAssets(categoryId),
    [categoryId],
  );

  const content = eventCategoryContent[categoryId];
  const seo = eventCategorySeo[categoryId];

  const servicesWithImages = useMemo(() => {
    if (!content?.subEvents) return [];
    return pairSubeventsWithGallery(content.subEvents, gallery);
  }, [content, gallery]);

  const whyChooseBannerImage = useMemo(
    () => findWhyChooseImage(gallery),
    [gallery],
  );

  useEffect(() => {
    const hash = (location.hash || window.location.hash).slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.pathname, location.hash]);

  if (!content || !seo) return null;

  const extra = content.additionalIntro
    ? `\n\n${content.additionalIntro.trim()}`
    : "";
  const introParagraphs = `${content.intro.trim()}${extra}`
    .trim()
    .split(/\n\n+/);

  const faqJsonLd = content.faq?.length
    ? buildFaqPageJsonLd(content.faq)
    : null;

  const breadcrumbItems = [
    { label: "Home", to: "/" },
    { label: seo.breadcrumbLabel },
  ];

  const breadcrumbJsonLd = buildBreadcrumbListJsonLd([
    { name: "Home", path: "/" },
    { name: seo.breadcrumbLabel, path: seo.path },
  ]);

  const categoryGraph = buildCategoryPageGraph({
    pagePath: seo.path,
    serviceName: seo.serviceName,
    serviceDescription: seo.serviceDescription,
    eventName: seo.eventName,
    eventDescription: seo.eventDescription,
  });

  return (
    <div className="event-category-page">
      {breadcrumbJsonLd ? <JsonLd data={breadcrumbJsonLd} /> : null}
      <JsonLd data={categoryGraph} />
      {faqJsonLd ? <JsonLd data={faqJsonLd} /> : null}

      <Breadcrumbs
        items={breadcrumbItems.map((b) =>
          b.to ? { label: b.label, to: b.to } : { label: b.label },
        )}
      />

      <PageBanner
        title={content.title}
        subtitle={content.tagline}
        image={bannerSrc}
        imageAlt={content.bannerImageAlt || ""}
        imageWidth={1920}
        imageHeight={1080}
      />

      <section className="event-category-section">
        <div className="event-category-container">
          <div className="event-category-intro">
            {introParagraphs.map((para, index) => (
              <p key={index}>{para}</p>
            ))}
          </div>

          {content.multilingualNote ? (
            <div
              className="event-category-lang"
              aria-label="Multilingual summary"
            >
              <p lang="hi" className="event-category-lang-p">
                {content.multilingualNote.hi}
              </p>
              <p lang="gu" className="event-category-lang-p">
                {content.multilingualNote.gu}
              </p>
            </div>
          ) : null}

          <section
            className="event-category-services"
            aria-labelledby="event-category-services-heading"
          >
            <div className="event-category-header event-category-services-header">
              <h2
                id="event-category-services-heading"
                className="event-category-heading"
              >
                Our Services
              </h2>
              <div className="title-underline" aria-hidden="true" />
              <p className="event-category-services-tagline">
                Premium planning and production—each service paired with real
                work from our portfolio.
              </p>
            </div>
            <div className="event-category-services-grid">
              {servicesWithImages.map((item, index) => (
                <article
                  key={`${item.name}-${index}`}
                  id={subeventNameToSlug(item.name)}
                  className={`event-category-service-row${
                    index % 2 === 1
                      ? " event-category-service-row--reverse"
                      : ""
                  }`}
                >
                  <div className="event-category-service__graphic">
                    {item.image ? (
                      <div className="home-graphic-wrap home-graphic-wrap--expertise-row">
                        <img
                          src={item.image.src}
                          alt={item.image.alt || item.name}
                          className="home-graphic-img"
                          width={800}
                          height={500}
                          loading={index > 0 ? "lazy" : "eager"}
                          decoding="async"
                        />
                      </div>
                    ) : (
                      <div
                        className="event-category-service__placeholder"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="event-category-service__body">
                    <h3 className="event-category-service__title">
                      {item.name}
                    </h3>
                    <p className="event-category-service__text">
                      {item.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div className="event-category-why">
            {whyChooseBannerImage ? (
              <div className="event-category-why-hero">
                <img
                  src={whyChooseBannerImage.src}
                  alt={`Why choose Panthi Event for ${content.title}`}
                  className="event-category-why-banner-img"
                  width={1280}
                  height={360}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
            <div className="event-category-why-shell">
              <div className="event-category-why-head">
                <h2 className="event-category-heading">
                  Why Choose Panthi Event
                </h2>
                <div className="title-underline" aria-hidden="true" />
              </div>
              <ul className="event-category-why-list">
                {content.whyChoose.map((item, index) => (
                  <li key={index} className="event-category-why-card">
                    <FaCheck className="event-category-check" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {content.faq?.length ? (
            <div className="event-category-faq">
              <div className="event-category-faq-head">
                <h2 className="event-category-heading">
                  Frequently asked questions
                </h2>
                <div className="title-underline" aria-hidden="true" />
                <p className="event-category-faq-lead">
                  Straight answers about planning, production, and how we work
                  with you.
                </p>
              </div>
              <div className="event-category-faq-list">
                {content.faq.map((item, index) => (
                  <details
                    key={index}
                    className="event-category-faq-details"
                    {...(index === 0 ? { open: true } : {})}
                  >
                    <summary className="event-category-faq-summary">
                      {item.question}
                    </summary>
                    <p className="event-category-faq-a">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default EventCategoryLayout;
